/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { mercuryBaseQuery } from 'api/base'
import {
    BillingCycle,
    LoginUrl,
    ModuleMeta,
    PendingServiceCancellation,
    PlanChanging,
    PlanChangingStatus,
    Service,
    ServiceCancellationCheck
} from 'models/service'
import { Invoice } from 'models/billing'

/**********************************************************************************************************
 *   BILLING API
 **********************************************************************************************************/
export const serviceAPI = createApi({
    reducerPath: 'serviceAPI',
    baseQuery: mercuryBaseQuery,
    tagTypes: ['Services', 'Service', 'Cancellation-Requests', 'Change-Plan-Status'],
    endpoints: (builder) => ({
        services: builder.query({
            query: (page = 1) => `client/services?page=${page}`,
            providesTags: ['Services']
        }),
        service: builder.query({
            query: (id) => `client/services/${id}`,
            transformResponse: (response: { data: Service }) => response.data,
            providesTags: ['Service']
        }),
        availablePlans: builder.query({
            query: (id) => `client/services/${id}/change-plan`,
            transformResponse: (response: { data: PlanChanging }) => {
                return {
                    plan_paths: response.data.plan_paths,
                    config_options: response.data.config_options.length >= 1
                        ? response.data.config_options[0].config_options
                        : []
                }
            }
        }),
        changePlanStatus: builder.query({
            query: (id) => `client/services/${id}/change-plan/status`,
            transformResponse: (response: { data: PlanChangingStatus }) => response.data,
            providesTags: ['Change-Plan-Status']
        }),
        changePlan: builder.mutation({
            query: ({ id, ...rest }) => ({
                url: `client/services/${id}/change-plan`,
                method: 'POST',
                body: rest
            }),
            transformResponse: (response: { data: Invoice }) => response.data,
            invalidatesTags: ['Change-Plan-Status']
        }),
        CancelChangePlanRequest: builder.mutation({
            query: (id) => ({
                url: `client/services/${id}/change-plan`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Change-Plan-Status']
        }),
        renewPrice: builder.query({
            query: (id) => `client/services/${id}/renew`,
            transformResponse: (response: { data: BillingCycle[] }) => response.data
        }),
        renewService: builder.mutation({
            query: ({ id, ...rest }) => ({
                url: `client/services/${id}/renew`,
                method: 'POST',
                body: rest
            }),
            transformResponse: (response: { data: Invoice }) => response.data
        }),
        billingCycle: builder.query({
            query: (id) => `client/services/${id}/billing-cycle`,
            transformResponse: (response: { data: BillingCycle[] }) => response.data
        }),
        changeBillingCycle: builder.mutation({
            query: ({ id, ...rest }) => ({
                url: `client/services/${id}/billing-cycle`,
                method: 'PUT',
                body: rest
            }),
            invalidatesTags: ['Services', 'Service']
        }),
        loginURL: builder.query({
            query: (id) => `client/services/${id}/login`,
            transformResponse: (response: { data: LoginUrl }) => response.data.url
        }),
        moduleMeta: builder.query({
            query: (id) => `client/services/${id}/module/meta`,
            transformResponse: (response: { data: ModuleMeta }) => response.data
        }),
        changePassword: builder.mutation({
            query: ({ id, ...rest }) => ({
                url: `client/services/${id}/module/change-password`,
                method: 'POST',
                body: rest
            })
        }),
        pendingServiceCancellation: builder.query({
            query: (id) => `client/services/${id}/cancel`,
            transformResponse: (response: { data: PendingServiceCancellation }) => response.data,
            providesTags: ['Cancellation-Requests']
        }),
        checkCancelableService: builder.query({
            query: (id) => `client/services/${id}/cancel/check`,
            transformResponse: (response: { data: ServiceCancellationCheck }) => response.data
        }),
        cancelService: builder.mutation({
            query: ({ id, ...rest }) => ({
                url: `client/services/${id}/cancel`,
                method: 'POST',
                body: rest
            }),
            invalidatesTags: ['Cancellation-Requests']
        }),
        deleteCancelRequest: builder.mutation({
            query: (id) => ({
                url: `client/services/${id}/cancel`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Cancellation-Requests']
        })
    })
})

export const {
    useServicesQuery,
    useServiceQuery,
    useAvailablePlansQuery,
    useChangePlanStatusQuery,
    useChangePlanMutation,
    useCancelChangePlanRequestMutation,
    useRenewPriceQuery,
    useRenewServiceMutation,
    useBillingCycleQuery,
    useChangeBillingCycleMutation,
    useLoginURLQuery,
    useModuleMetaQuery,
    useChangePasswordMutation,
    usePendingServiceCancellationQuery,
    useCheckCancelableServiceQuery,
    useCancelServiceMutation,
    useDeleteCancelRequestMutation
} = serviceAPI
